import React, { useState, useEffect, useRef, useCallback } from "react";
import { useReactToPrint } from "react-to-print";
import CustomModal from "./CustomModal";
import AuthInput from "../Input/AuthInput";
import { useDispatch, useSelector } from "react-redux";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import {
  CreateCustomerPaymentApi,
  CreateSupplierPaymentApi,
  CreateLoanApi,
} from "../../ApiRequests";
import AuthBtn from "../Buttons/AuthBtn";
import CustomerSelector from "../Selector/CustomerSelector";
import CompanySelector from "../Selector/CompanySelector";
import TypeSelector from "../Selector/TypeSelector";
import LoanPartySelector from "../Selector/LoanPartySelector";
import { fetchCustomers } from "../../store/Slices/CustomerSlice";
import { fetchSuppliers } from "../../store/Slices/SupplierSlice";
import { fetchAccounts } from "../../store/Slices/AccountSlice";
import { fetchLoanLog, fetchLoans } from "../../store/Slices/LoanSlice";
import PaymentAccountSelector from "../Selector/PaymentAccountSelector";
import ThermalCustomerPayment from "../Reports/ThermalCustomerPayment";
import moment from "moment";
import { FiSend } from "react-icons/fi";
import { getLoanBalance, LOAN_TYPE_GIVEN } from "../../utils/LoanConverter";
import { toUnixSeconds } from "../../utils/dateTime";

const ENTITY_CUSTOMER = "Customer";
const ENTITY_COMPANY = "Company";

const CUST_TYPE_PAYMENT = "Payment";
const CUST_TYPE_VENDOR = "Transfer to Vendor";
const CUST_TYPE_LOAN = "Transfer to Loan Party";

const CUSTOMER_PAYMENT_TYPES = [
  { label: CUST_TYPE_PAYMENT },
  { label: CUST_TYPE_VENDOR },
  { label: CUST_TYPE_LOAN },
];

const userData = JSON.parse(localStorage.getItem("user")) || {};
const { branchId = "" } = userData;

const toLocalDateTimeValue = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/** Short numeric bill # (e.g. 831143052) — not PAY-YYYYMMDD-HHMMSS. */
const makeShortBillNo = () =>
  `${String(Date.now()).slice(-7)}${Math.floor(Math.random() * 10)}`;

const resolvePaymentInvoiceNo = (value) => {
  const v = String(value || "").trim();
  if (v && !/^auto$/i.test(v) && !/^PAY-/i.test(v)) return v;
  return makeShortBillNo();
};

const AddPaymentModal = ({
  Open,
  setOpen,
  presetEntityType,
  presetEntity,
  onSuccess,
}) => {
  const [SelectedCustomer, setSelectedCustomer] = useState("");
  const [SelectedSupplier, setSelectedSupplier] = useState("");
  const [SelectedLoan, setSelectedLoan] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [depositor, setDepositor] = useState("");
  const AccountState = useSelector((state) => state.AccountState);
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [marka, setMarka] = useState("");
  const [bichakNo, setBichakNo] = useState("");
  const [CurDate, setCurDate] = useState(() => toLocalDateTimeValue());
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(ENTITY_CUSTOMER);
  const [customerTxnType, setCustomerTxnType] = useState(CUST_TYPE_PAYMENT);
  const [thermalReceiptData, setThermalReceiptData] = useState(null);
  const thermalPrintRef = useRef(null);
  const silentPrintPendingRef = useRef(false);

  const dispatch = useDispatch();

  const isCustomerEntity = activeTab === ENTITY_CUSTOMER;
  const isCompanyEntity = activeTab === ENTITY_COMPANY;
  const isCustomerPayment =
    isCustomerEntity && customerTxnType === CUST_TYPE_PAYMENT;
  const isTransferToVendor =
    isCustomerEntity && customerTxnType === CUST_TYPE_VENDOR;
  const isTransferToLoan =
    isCustomerEntity && customerTxnType === CUST_TYPE_LOAN;
  const isVendorPayment = isCompanyEntity || isTransferToVendor;
  const isLoanPayment = isTransferToLoan;
  const isTransferFlow = isTransferToVendor || isTransferToLoan;
  const requiresAccount = !isTransferFlow;

  const buildReceiptData = useCallback(
    (saved) => ({
      date: saved?.date
        ? moment.unix(saved.date).format("YYYY-MM-DD HH:mm")
        : CurDate,
      invoice_no: saved?.invoice_no ?? depositor,
      customer_name:
        saved?.customer_name ?? SelectedCustomer?.name ?? "",
      payment_type:
        saved?.payment_type ?? (Number(selectedAccount?.type) === 1 ? 1 : 2),
      bank_name: saved?.bank_name ?? selectedAccount?.account_name ?? "",
      bank_number: saved?.bank_number ?? selectedAccount?.account_no ?? "",
      amount: Number(saved?.amount ?? amount),
      desc: saved?.desc ?? desc,
    }),
    [
      CurDate,
      depositor,
      SelectedCustomer?.name,
      selectedAccount,
      amount,
      desc,
    ]
  );

  const refreshAfterSuccess = useCallback(() => {
    if (isCustomerEntity) dispatch(fetchCustomers());
    if (isVendorPayment) dispatch(fetchSuppliers());
    if (isLoanPayment) {
      dispatch(fetchLoans());
      dispatch(fetchLoanLog());
    }
    dispatch(fetchAccounts());
    if (typeof onSuccess === "function") onSuccess();
  }, [
    dispatch,
    isCustomerEntity,
    isVendorPayment,
    isLoanPayment,
    onSuccess,
  ]);

  const handleSilentThermalPrint = useReactToPrint({
    content: () => thermalPrintRef.current,
    documentTitle: `Payment-${thermalReceiptData?.invoice_no || "receipt"}`,
    removeAfterPrint: true,
    onAfterPrint: () => {
      silentPrintPendingRef.current = false;
      setThermalReceiptData(null);
      setOpen(false);
      refreshAfterSuccess();
    },
  });

  const queueSilentPrint = (receipt) => {
    silentPrintPendingRef.current = true;
    setThermalReceiptData(receipt);
  };

  useEffect(() => {
    if (!silentPrintPendingRef.current || !thermalReceiptData) return;
    silentPrintPendingRef.current = false;
    const timer = setTimeout(() => handleSilentThermalPrint(), 350);
    return () => clearTimeout(timer);
  }, [thermalReceiptData, handleSilentThermalPrint]);

  useEffect(() => {
    if (!Open) {
      setThermalReceiptData(null);
      silentPrintPendingRef.current = false;
      setSelectedAccount("");
      setSelectedLoan("");
      setAmount("");
      setDesc("");
      setDepositor("");
      setMarka("");
      setBichakNo("");
      setCurDate(toLocalDateTimeValue());
      setCustomerTxnType(CUST_TYPE_PAYMENT);
      return;
    }
    setDepositor(makeShortBillNo());
  }, [Open]);

  useEffect(() => {
    if (!Open) return;
    dispatch(fetchAccounts());
  }, [Open, dispatch]);

  useEffect(() => {
    if (!Open || selectedAccount?._id) return;
    // Transfers: bank/account is optional — do not auto-select Cash
    if (isTransferFlow) return;
    const accounts = Array.isArray(AccountState.data) ? AccountState.data : [];
    const cashAccount =
      accounts.find(
        (row) => String(row.account_name || "").toUpperCase() === "CASH"
      ) || accounts[0];
    if (cashAccount) setSelectedAccount(cashAccount);
  }, [Open, selectedAccount, AccountState.data, isTransferFlow]);

  const isPreset =
    (presetEntityType === "customer" || presetEntityType === "company") &&
    presetEntity?._id;

  useEffect(() => {
    if (!Open) return;
    if (presetEntityType === "customer" && presetEntity?._id) {
      setActiveTab(ENTITY_CUSTOMER);
      setCustomerTxnType(CUST_TYPE_PAYMENT);
      setSelectedCustomer(presetEntity);
      setSelectedSupplier("");
      setSelectedLoan("");
    } else if (presetEntityType === "company" && presetEntity?._id) {
      setActiveTab(ENTITY_COMPANY);
      setCustomerTxnType(CUST_TYPE_PAYMENT);
      setSelectedSupplier(presetEntity);
      setSelectedCustomer("");
      setSelectedLoan("");
    } else {
      setSelectedCustomer("");
      setSelectedSupplier("");
      setSelectedLoan("");
      setActiveTab(ENTITY_CUSTOMER);
      setCustomerTxnType(CUST_TYPE_PAYMENT);
    }
  }, [Open, presetEntityType, presetEntity]);

  const handleEntityChange = (next) => {
    setActiveTab(next);
    setSelectedCustomer("");
    setSelectedSupplier("");
    setSelectedLoan("");
    setCustomerTxnType(CUST_TYPE_PAYMENT);
  };

  const handleCustomerTxnChange = (next) => {
    setCustomerTxnType(next);
    // Keep selected customer — only clear destination party
    setSelectedSupplier("");
    setSelectedLoan("");
    if (next === CUST_TYPE_VENDOR || next === CUST_TYPE_LOAN) {
      setSelectedAccount("");
    }
    if (next !== CUST_TYPE_LOAN) {
      setMarka("");
      setBichakNo("");
    }
  };

  const onSubmit = async (e, submitType = 1) => {
    e.preventDefault();
    setLoading(true);
    const shouldPrint = submitType === 2 && isCustomerPayment;
    const parsedAmount = Number(amount);

    if (requiresAccount && !selectedAccount?._id) {
      WarningToast("Select payment account");
      setLoading(false);
      return;
    }
    if (!parsedAmount || isNaN(parsedAmount) || parsedAmount <= 0) {
      WarningToast("Enter a valid Amount");
      setLoading(false);
      return;
    }

    try {
      let response;

      if (isLoanPayment) {
        if (!SelectedCustomer?._id) {
          WarningToast("Select a Customer");
          setLoading(false);
          return;
        }
        if (!SelectedLoan?._id) {
          WarningToast("Select a loan party");
          setLoading(false);
          return;
        }
        if (!marka?.trim()) {
          WarningToast("Enter Marka");
          setLoading(false);
          return;
        }

        const transferDesc =
          desc?.trim() ||
          `Transfer to Loan Party: ${SelectedLoan.partyName || ""}`;

        // 1) Customer ledger
        const customerRes = await CreateCustomerPaymentApi({
          invoice_no: resolvePaymentInvoiceNo(depositor),
          desc: transferDesc,
          amount: parsedAmount,
          date: toUnixSeconds(CurDate),
          customerId: SelectedCustomer._id,
          customer_name: SelectedCustomer.name,
          txn_type: customerTxnType,
          party_name: SelectedLoan.partyName || "",
          partyId: SelectedLoan._id,
          skipAccount: true,
        });
        if (!customerRes.data.success) {
          ErrorToast(customerRes.data.error?.msg || "Customer entry failed");
          setLoading(false);
          return;
        }
        const customerPayNo =
          customerRes.data?.data?.payload?.invoice_no ||
          customerRes.data?.data?.invoice_no ||
          "";
        if (customerPayNo) setDepositor(customerPayNo);

        // 2) Loan Given (disbursement) — not repayment
        response = await CreateLoanApi({
          loanAccountId: SelectedLoan._id,
          partyName: SelectedLoan.partyName,
          phone: SelectedLoan.phone || "",
          loanType: LOAN_TYPE_GIVEN,
          amount: parsedAmount,
          branchId,
          date: toUnixSeconds(CurDate),
          marka: marka.trim(),
          bichak_no: bichakNo.trim() || undefined,
          desc: transferDesc,
          skipAccount: true,
        });
      } else {
        if (isCustomerEntity && !SelectedCustomer?._id) {
          WarningToast("Select a Customer");
          setLoading(false);
          return;
        }
        if (isVendorPayment && !SelectedSupplier) {
          WarningToast("Select a Vendor");
          setLoading(false);
          return;
        }

        const hasAccount = Boolean(selectedAccount?._id);
        const paymentTypeNum = hasAccount
          ? Number(selectedAccount.type) === 1
            ? 1
            : 2
          : 1;

        if (isCustomerPayment) {
          response = await CreateCustomerPaymentApi({
            invoice_no: resolvePaymentInvoiceNo(depositor),
            payment_type: paymentTypeNum,
            bank_name:
              paymentTypeNum === 2 ? selectedAccount.account_name : "",
            bank_number:
              paymentTypeNum === 2 ? selectedAccount.account_no : "",
            accountId: selectedAccount._id,
            desc,
            amount: parsedAmount,
            date: toUnixSeconds(CurDate),
            customerId: SelectedCustomer._id,
            customer_name: SelectedCustomer.name,
            txn_type: customerTxnType,
          });
        } else if (isTransferToVendor) {
          const transferDesc =
            desc?.trim() ||
            `Transfer to Vendor: ${SelectedSupplier.name || ""}`;
          const customerPayNo = resolvePaymentInvoiceNo(depositor);

          // 1) Customer ledger
          const customerRes = await CreateCustomerPaymentApi({
            invoice_no: customerPayNo,
            desc: transferDesc,
            amount: parsedAmount,
            date: toUnixSeconds(CurDate),
            customerId: SelectedCustomer._id,
            customer_name: SelectedCustomer.name,
            txn_type: customerTxnType,
            party_name: SelectedSupplier.name || "",
            partyId: SelectedSupplier._id,
            skipAccount: true,
          });
          if (!customerRes.data.success) {
            ErrorToast(customerRes.data.error?.msg || "Customer entry failed");
            setLoading(false);
            return;
          }
          const savedPayNo =
            customerRes.data?.data?.payload?.invoice_no ||
            customerRes.data?.data?.invoice_no ||
            customerPayNo;
          if (savedPayNo) setDepositor(savedPayNo);

          // 2) Vendor ledger — same base # with -V
          response = await CreateSupplierPaymentApi({
            invoice_no: `${savedPayNo}-V`,
            desc: transferDesc,
            amount: parsedAmount,
            date: toUnixSeconds(CurDate),
            companyId: SelectedSupplier._id,
            company_name: SelectedSupplier.name,
            customerId: SelectedCustomer._id,
            customer_name: SelectedCustomer.name,
            skipAccount: true,
            txn_type: customerTxnType,
          });
        } else {
          // Company (Vendor) payment — not a customer transfer
          response = await CreateSupplierPaymentApi({
            invoice_no: resolvePaymentInvoiceNo(depositor),
            desc,
            amount: parsedAmount,
            date: toUnixSeconds(CurDate),
            companyId: SelectedSupplier._id,
            company_name: SelectedSupplier.name,
            payment_type: paymentTypeNum,
            bank_name:
              paymentTypeNum === 2 ? selectedAccount.account_name : "",
            bank_number:
              paymentTypeNum === 2 ? selectedAccount.account_no : "",
            accountId: selectedAccount._id,
          });
        }
      }

      if (response.data.success) {
        SuccessToast(
          isLoanPayment
            ? "Loan given recorded"
            : "Payment added successfully"
        );
        const saved =
          response.data?.data?.payload ?? response.data?.data ?? null;
        if (shouldPrint) {
          queueSilentPrint(buildReceiptData(saved));
        } else {
          setOpen(false);
          refreshAfterSuccess();
        }
      } else {
        ErrorToast(response.data.error?.msg);
      }
    } catch (err) {
      console.error(err);
      ErrorToast(
        err.response?.data?.error?.msg ||
          (isLoanPayment
            ? "Failed to record loan given"
            : "Failed to add payment")
      );
    }
    setLoading(false);
  };

  return (
    <CustomModal open={Open} setOpen={setOpen}>
      <div className="pb-8">
        <h1 className="w-full text-center font-[700] text-3xl py-8">
          Add Payment
        </h1>
        <div className="flex gap-4 max696:flex-col px-10 max696:max-h-[70vh] max696:overflow-y-auto">
          <div className="flex flex-col gap-y-4">
            {!isPreset && (
              <TypeSelector
                label="Party type"
                activeTab={activeTab}
                setActiveTab={handleEntityChange}
                placeholder="Select Customer / Company"
              />
            )}

            {isPreset ? (
              <div className="px-4 py-3 rounded-lg border border-sec2/40 bg-gradient-to-r from-sec2/10 to-transparent font-quicksand font-semibold text-main">
                {presetEntityType === "customer" ? "Customer" : "Company"}:{" "}
                <span className="text-ink">{presetEntity.name}</span>
              </div>
            ) : null}

            {/* Customer-only subtypes — never shown for Vendor/Company */}
            {isCustomerEntity && (
              <TypeSelector
                label="Customer payment type"
                activeTab={customerTxnType}
                setActiveTab={handleCustomerTxnChange}
                options={CUSTOMER_PAYMENT_TYPES}
                placeholder="Select payment type"
              />
            )}

            {!isPreset && isCustomerEntity && (
              <CustomerSelector
                SelectedCustomer={SelectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
              />
            )}

            {(!isPreset || isTransferToVendor) && isVendorPayment && (
              <CompanySelector
                SelectedSupplier={SelectedSupplier}
                setSelectedSupplier={setSelectedSupplier}
              />
            )}

            {isLoanPayment && (
              <LoanPartySelector
                selectedParty={SelectedLoan}
                setSelectedParty={setSelectedLoan}
                label="Loan party"
                placeholder="Select loan party"
              />
            )}

            {!isTransferFlow && (
              <PaymentAccountSelector
                label="Payment account"
                selectedAccount={selectedAccount}
                setSelectedAccount={setSelectedAccount}
                placeholder="Select account (Cash / Bank)"
              />
            )}

            <AuthInput
              label="Description"
              placeholder="Enter Description"
              Value={desc}
              setValue={setDesc}
            />
          </div>
          <div className="flex flex-col gap-y-4">
            <AuthInput
              label="Date & Time"
              placeholder="Select Date & Time"
              Type="datetime-local"
              Value={CurDate}
              setValue={setCurDate}
              Width="w-full sm:w-[320px]"
            />

            {/* Auto-generated short numeric invoice # */}
            <AuthInput
              label="Auto Invoice #"
              placeholder="Auto #"
              Value={depositor}
              setValue={setDepositor}
              readonly
            />

            {isCustomerPayment && SelectedCustomer?._id && (
              <div className="px-4 py-3 rounded-lg border border-line bg-subtle/50 font-quicksand text-sm space-y-1.5">
                <div className="flex justify-between gap-3">
                  <span className="text-inkSecondary font-semibold">
                    Before Payment
                  </span>
                  <span className="font-bold text-ink tabular-nums">
                    Rs{" "}
                    {Number(SelectedCustomer.recieveable || 0).toLocaleString(
                      "en-PK"
                    )}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-inkSecondary font-semibold">
                    After Payment
                  </span>
                  <span className="font-bold text-main tabular-nums">
                    Rs{" "}
                    {(
                      Number(SelectedCustomer.recieveable || 0) -
                      (Number(amount) > 0 ? Number(amount) : 0)
                    ).toLocaleString("en-PK")}
                  </span>
                </div>
              </div>
            )}

            {isLoanPayment && (
              <>
                <AuthInput
                  label="Marka"
                  placeholder="Enter Marka"
                  Value={marka}
                  setValue={setMarka}
                />
                <AuthInput
                  label="Bichak No (optional)"
                  placeholder="Enter Bichak number"
                  Value={bichakNo}
                  setValue={setBichakNo}
                />
              </>
            )}

            {isLoanPayment && SelectedLoan?._id && (
              <div className="px-4 py-3 rounded-lg border border-line bg-subtle/40 font-quicksand text-sm text-inkSecondary">
                Current Loan Given balance:{" "}
                <span className="font-bold text-main">
                  Rs {getLoanBalance(SelectedLoan).toLocaleString("en-PK")}
                </span>
              </div>
            )}

            <AuthInput
              label="Amount"
              placeholder="Enter Amount"
              Type="number"
              Value={amount}
              setValue={setAmount}
            />
          </div>
        </div>
        <div className="flex justify-center items-center gap-x-2 mt-5 px-5">
          {loading ? (
            <AddingLightLoader />
          ) : (
            <>
              <AuthBtn
                title={"Cancel"}
                onSubmit={() => setOpen(false)}
                type={2}
              />
              {isCustomerPayment && (
                <button
                  type="button"
                  onClick={(e) => onSubmit(e, 2)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 h-11 rounded-lg border-2 border-main bg-card text-main text-sm font-bold hover:bg-brandSoft transition-colors active:scale-[.98] disabled:opacity-50"
                >
                  <FiSend size={16} />
                  Add &amp; Print
                </button>
              )}
              <AuthBtn
                title={isLoanPayment ? "Add Loan Given" : "Add Payment"}
                onSubmit={(e) => onSubmit(e, 1)}
                type={1}
              />
            </>
          )}
        </div>
      </div>

      {thermalReceiptData && (
        <div
          aria-hidden
          className="fixed pointer-events-none opacity-0"
          style={{ left: -10000, top: 0 }}
        >
          <ThermalCustomerPayment
            ref={thermalPrintRef}
            data={thermalReceiptData}
          />
        </div>
      )}
    </CustomModal>
  );
};

export default AddPaymentModal;
